import { z } from 'zod'

export const EXPLORE_SORTS = ['all', 'newest', 'popular', 'rated', 'saved'] as const
export type ExploreSort = (typeof EXPLORE_SORTS)[number]

export const EXPLORE_PRICES = ['any', 'free', 'paid'] as const
export type ExplorePrice = (typeof EXPLORE_PRICES)[number]

export const EXPLORE_LEVELS = ['beginner', 'intermediate', 'advanced'] as const
export type ExploreLevel = (typeof EXPLORE_LEVELS)[number]

export interface ExploreFilters {
  sort: ExploreSort
  price: ExplorePrice
  levels: ExploreLevel[]
  categories: string[]
}

/** Minimal shape the explore filter/sort helpers read from a course row. */
export interface CatalogCourse {
  difficultyLevel: string | null
  priceCents: number | null
  categoryName: string | null
  publishedAt: string | null
  rating: number
  ratingCount: number
  enrollCount: number
  favorited: boolean
}

const sortSchema = z.enum(EXPLORE_SORTS).catch('all')
const priceSchema = z.enum(EXPLORE_PRICES).catch('any')

function toList(value: string | string[] | undefined): string[] {
  if (!value)
    return []
  const raw = Array.isArray(value) ? value : value.split(',')
  return raw.map(v => v.trim()).filter(Boolean)
}

/**
 * Parse the explore-page query string into a validated filter set. Unknown or
 * malformed values fall back to defaults so a hand-edited URL never throws.
 */
export function parseExploreSearchParams(
  raw: Record<string, string | string[] | undefined>,
): ExploreFilters {
  const levels = toList(raw.level).filter((l): l is ExploreLevel =>
    (EXPLORE_LEVELS as readonly string[]).includes(l),
  )
  return {
    sort: sortSchema.parse(raw.sort),
    price: priceSchema.parse(raw.price),
    levels,
    categories: toList(raw.category),
  }
}

export function matchesExploreFilters(
  c: CatalogCourse,
  f: ExploreFilters,
): boolean {
  if (
    f.levels.length > 0
    && (!c.difficultyLevel
      || !f.levels.includes(c.difficultyLevel as ExploreLevel))
  ) {
    return false
  }
  if (f.price === 'free' && (c.priceCents ?? 0) !== 0)
    return false
  if (f.price === 'paid' && (c.priceCents ?? 0) <= 0)
    return false
  if (
    f.categories.length > 0
    && (!c.categoryName || !f.categories.includes(c.categoryName))
  ) {
    return false
  }
  if (f.sort === 'saved' && !c.favorited)
    return false
  return true
}

export function compareExploreCourses(
  a: CatalogCourse,
  b: CatalogCourse,
  sort: ExploreSort,
): number {
  switch (sort) {
    case 'newest':
      return (b.publishedAt ?? '').localeCompare(a.publishedAt ?? '')
    case 'popular':
      return b.enrollCount - a.enrollCount || b.ratingCount - a.ratingCount
    case 'rated':
      return b.rating - a.rating || b.ratingCount - a.ratingCount
    default:
      return 0
  }
}
